import { useState, useMemo } from 'react';
import { 
  Shield, TrendingUp, TrendingDown, Minus, CheckCircle, AlertTriangle,
  Filter, Globe, Users, Gamepad2, BookOpen, MessageSquare, Video,
  ShieldAlert, ShieldCheck, ShieldOff, Activity, Eye, Clock
} from 'lucide-react';

// Sample platform data with detailed metrics
const SAMPLE_PLATFORMS = [
  {
    id: 1,
    name: 'KidSafe Social',
    category: 'Social Media',
    location: 'United States',
    ageGroups: ['6-12', '13-17'],
    score: 92,
    trend: 'up',
    change: '+4',
    certLevel: 'endorsed',
    lastAudit: 'Mar 2025',
    metrics: {
      threatsBlocked: 15420,
      threatsBlockedToday: 342,
      avgResponseTime: '0.8s',
      falsePositiveRate: 2.1,
      groomingDetected: 892,
      extortionPrevented: 156,
      escalationsTriggered: 45,
      interventionsTier1: 8934,
      interventionsTier2: 2341,
      interventionsTier3: 423,
      interventionsTier4: 12
    }
  },
  {
    id: 2,
    name: 'EduLearn Platform',
    category: 'EdTech',
    location: 'United Kingdom',
    ageGroups: ['6-12', '13-17'],
    score: 88,
    trend: 'up',
    change: '+6',
    certLevel: 'endorsed',
    lastAudit: 'Mar 2025',
    metrics: {
      threatsBlocked: 8230,
      threatsBlockedToday: 156,
      avgResponseTime: '1.2s',
      falsePositiveRate: 3.2,
      groomingDetected: 234,
      extortionPrevented: 45,
      escalationsTriggered: 23,
      interventionsTier1: 5621,
      interventionsTier2: 1234,
      interventionsTier3: 189,
      interventionsTier4: 6
    }
  },
  {
    id: 3,
    name: 'GameZone Kids',
    category: 'Gaming',
    location: 'Australia',
    ageGroups: ['6-12', '13-17', '18+'],
    score: 81,
    trend: 'stable',
    change: '0',
    certLevel: 'certified',
    lastAudit: 'Feb 2025',
    metrics: {
      threatsBlocked: 23450,
      threatsBlockedToday: 523,
      avgResponseTime: '1.5s',
      falsePositiveRate: 4.8,
      groomingDetected: 1245,
      extortionPrevented: 312,
      escalationsTriggered: 89,
      interventionsTier1: 12345,
      interventionsTier2: 4532,
      interventionsTier3: 678,
      interventionsTier4: 23
    }
  },
  {
    id: 4,
    name: 'FamilyChat',
    category: 'Messaging',
    location: 'Canada',
    ageGroups: ['6-12', '13-17', '18+'],
    score: 76,
    trend: 'up',
    change: '+3',
    certLevel: 'certified',
    lastAudit: 'Feb 2025',
    metrics: {
      threatsBlocked: 6780,
      threatsBlockedToday: 89,
      avgResponseTime: '2.1s',
      falsePositiveRate: 5.6,
      groomingDetected: 456,
      extortionPrevented: 78,
      escalationsTriggered: 34,
      interventionsTier1: 3456,
      interventionsTier2: 987,
      interventionsTier3: 234,
      interventionsTier4: 8
    }
  },
  {
    id: 5,
    name: 'StreamSafe',
    category: 'Video',
    location: 'Germany',
    ageGroups: ['13-17', '18+'],
    score: 71,
    trend: 'down',
    change: '-2',
    certLevel: 'ready',
    lastAudit: 'Jan 2025',
    metrics: {
      threatsBlocked: 4560,
      threatsBlockedToday: 67,
      avgResponseTime: '2.8s',
      falsePositiveRate: 7.2,
      groomingDetected: 189,
      extortionPrevented: 34,
      escalationsTriggered: 12,
      interventionsTier1: 2345,
      interventionsTier2: 678,
      interventionsTier3: 145,
      interventionsTier4: 4
    }
  },
  {
    id: 6,
    name: 'LearnTogether',
    category: 'EdTech',
    location: 'Australia',
    ageGroups: ['6-12'],
    score: 85,
    trend: 'up',
    change: '+5',
    certLevel: 'certified',
    lastAudit: 'Mar 2025',
    metrics: {
      threatsBlocked: 3210,
      threatsBlockedToday: 45,
      avgResponseTime: '0.9s',
      falsePositiveRate: 2.8,
      groomingDetected: 98,
      extortionPrevented: 12,
      escalationsTriggered: 8,
      interventionsTier1: 2134,
      interventionsTier2: 456,
      interventionsTier3: 67,
      interventionsTier4: 2
    }
  },
  {
    id: 7,
    name: 'PlayPals',
    category: 'Gaming',
    location: 'United States',
    ageGroups: ['6-12', '13-17'],
    score: 79,
    trend: 'up',
    change: '+2',
    certLevel: 'certified',
    lastAudit: 'Feb 2025',
    metrics: {
      threatsBlocked: 18920,
      threatsBlockedToday: 412,
      avgResponseTime: '1.3s',
      falsePositiveRate: 4.1,
      groomingDetected: 876,
      extortionPrevented: 198,
      escalationsTriggered: 56,
      interventionsTier1: 9876,
      interventionsTier2: 3456,
      interventionsTier3: 512,
      interventionsTier4: 18
    }
  },
  {
    id: 8,
    name: 'TeenTalk',
    category: 'Social Media',
    location: 'United Kingdom',
    ageGroups: ['13-17'],
    score: 68,
    trend: 'down',
    change: '-4',
    certLevel: 'ready',
    lastAudit: 'Jan 2025',
    metrics: {
      threatsBlocked: 9870,
      threatsBlockedToday: 234,
      avgResponseTime: '3.2s',
      falsePositiveRate: 8.9,
      groomingDetected: 567,
      extortionPrevented: 89,
      escalationsTriggered: 45,
      interventionsTier1: 5432,
      interventionsTier2: 1987,
      interventionsTier3: 398,
      interventionsTier4: 15
    }
  }
];

const CATEGORIES = ['All', 'Social Media', 'EdTech', 'Gaming', 'Messaging', 'Video'];
const LOCATIONS = ['All', 'United States', 'United Kingdom', 'Australia', 'Canada', 'Germany'];
const AGE_GROUPS = ['All', '6-12', '13-17', '18+'];

const categoryIcons = {
  'Social Media': Users,
  'EdTech': BookOpen,
  'Gaming': Gamepad2,
  'Messaging': MessageSquare,
  'Video': Video
};

export const SAFEScoreDashboard = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('All');
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  // Filter platforms based on selections
  const filteredPlatforms = useMemo(() => {
    return SAMPLE_PLATFORMS.filter(platform => {
      if (selectedCategory !== 'All' && platform.category !== selectedCategory) return false;
      if (selectedLocation !== 'All' && platform.location !== selectedLocation) return false;
      if (selectedAgeGroup !== 'All' && !platform.ageGroups.includes(selectedAgeGroup)) return false;
      return true;
    });
  }, [selectedCategory, selectedLocation, selectedAgeGroup]);

  // Calculate aggregate stats
  const aggregateStats = useMemo(() => {
    const total = filteredPlatforms.reduce((acc, p) => ({
      threatsBlocked: acc.threatsBlocked + p.metrics.threatsBlocked,
      threatsBlockedToday: acc.threatsBlockedToday + p.metrics.threatsBlockedToday,
      groomingDetected: acc.groomingDetected + p.metrics.groomingDetected,
      extortionPrevented: acc.extortionPrevented + p.metrics.extortionPrevented,
      tier1: acc.tier1 + p.metrics.interventionsTier1,
      tier2: acc.tier2 + p.metrics.interventionsTier2,
      tier3: acc.tier3 + p.metrics.interventionsTier3,
      tier4: acc.tier4 + p.metrics.interventionsTier4,
    }), { threatsBlocked: 0, threatsBlockedToday: 0, groomingDetected: 0, extortionPrevented: 0, tier1: 0, tier2: 0, tier3: 0, tier4: 0 });
    
    const avgScore = filteredPlatforms.length > 0 
      ? Math.round(filteredPlatforms.reduce((acc, p) => acc + p.score, 0) / filteredPlatforms.length)
      : 0;
    
    return { ...total, avgScore, platformCount: filteredPlatforms.length };
  }, [filteredPlatforms]);

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500 bg-emerald-50';
    if (score >= 60) return 'text-cyan-600 bg-cyan-50';
    if (score >= 40) return 'text-amber-500 bg-amber-50';
    return 'text-red-500 bg-red-50';
  };

  const getCertBadgeColor = (level) => {
    const colors = {
      'endorsed': 'bg-amber-600 text-white',
      'certified': 'bg-cyan-700 text-white',
      'ready': 'bg-slate-500 text-white'
    };
    return colors[level] || 'bg-slate-300 text-slate-700';
  };

  return (
    <div className="bg-slate-50 border border-slate-200" data-testid="safescore-dashboard">
      {/* Dashboard Header */}
      <div className="bg-slate-900 text-white p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-cyan-400" />
            <div>
              <h3 className="text-xl font-semibold">SAFEScore Dashboard</h3>
              <p className="text-sm text-slate-400">Real-time platform performance monitoring</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2">
            <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
            <span className="text-sm font-medium">Sample Data</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filter Platforms</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Category Filter */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Platform Type</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full border border-slate-300 bg-white p-3 text-slate-900 focus:outline-none focus:border-cyan-600"
              data-testid="filter-category"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Location Filter */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Location</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="w-full border border-slate-300 bg-white p-3 text-slate-900 focus:outline-none focus:border-cyan-600"
              data-testid="filter-location"
            >
              {LOCATIONS.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          {/* Age Group Filter */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Age Group</label>
            <select
              value={selectedAgeGroup}
              onChange={(e) => setSelectedAgeGroup(e.target.value)}
              className="w-full border border-slate-300 bg-white p-3 text-slate-900 focus:outline-none focus:border-cyan-600"
              data-testid="filter-age-group"
            >
              {AGE_GROUPS.map(age => (
                <option key={age} value={age}>{age}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Aggregate Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-slate-200">
        <div className="bg-slate-900 text-white p-6 border-r border-slate-700">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Platforms Monitored</p>
          <p className="text-3xl font-bold">{aggregateStats.platformCount}</p>
        </div>
        <div className="bg-slate-900 text-white p-6 border-r border-slate-700">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Avg SAFEScore</p>
          <p className="text-3xl font-bold text-cyan-400">{aggregateStats.avgScore}</p>
        </div>
        <div className="bg-emerald-600 text-white p-6 border-r border-emerald-500">
          <p className="text-xs uppercase tracking-wider text-emerald-200 mb-1">Threats Blocked (Total)</p>
          <p className="text-3xl font-bold">{aggregateStats.threatsBlocked.toLocaleString()}</p>
        </div>
        <div className="bg-amber-600 text-white p-6">
          <p className="text-xs uppercase tracking-wider text-amber-200 mb-1">Blocked Today</p>
          <p className="text-3xl font-bold">{aggregateStats.threatsBlockedToday.toLocaleString()}</p>
        </div>
      </div>

      {/* Threat Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-white border-b border-slate-200">
        <div className="text-center p-4 bg-red-50 border border-red-200">
          <ShieldAlert className="w-8 h-8 text-red-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-red-600">{aggregateStats.groomingDetected.toLocaleString()}</p>
          <p className="text-xs uppercase tracking-wider text-red-500">Grooming Detected</p>
        </div>
        <div className="text-center p-4 bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-amber-600">{aggregateStats.extortionPrevented.toLocaleString()}</p>
          <p className="text-xs uppercase tracking-wider text-amber-500">Extortion Prevented</p>
        </div>
        <div className="text-center p-4 bg-cyan-50 border border-cyan-200">
          <ShieldCheck className="w-8 h-8 text-cyan-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-cyan-600">{(aggregateStats.tier1 + aggregateStats.tier2).toLocaleString()}</p>
          <p className="text-xs uppercase tracking-wider text-cyan-500">Early Interventions</p>
        </div>
        <div className="text-center p-4 bg-purple-50 border border-purple-200">
          <ShieldOff className="w-8 h-8 text-purple-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-purple-600">{aggregateStats.tier4}</p>
          <p className="text-xs uppercase tracking-wider text-purple-500">Crisis Escalations</p>
        </div>
      </div>

      {/* Intervention Tiers Breakdown */}
      <div className="p-6 bg-slate-50 border-b border-slate-200">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">Intervention Distribution</p>
        <div className="flex items-end gap-2 h-24">
          <div className="flex-1 flex flex-col items-center">
            <div 
              className="w-full bg-slate-300 transition-all" 
              style={{ height: `${Math.min(100, (aggregateStats.tier1 / (aggregateStats.tier1 || 1)) * 100)}%` }}
            ></div>
            <p className="text-xs mt-2 text-slate-600">Tier 1</p>
            <p className="text-sm font-bold">{aggregateStats.tier1.toLocaleString()}</p>
          </div>
          <div className="flex-1 flex flex-col items-center">
            <div 
              className="w-full bg-cyan-400 transition-all" 
              style={{ height: `${Math.min(100, (aggregateStats.tier2 / (aggregateStats.tier1 || 1)) * 100)}%` }}
            ></div>
            <p className="text-xs mt-2 text-slate-600">Tier 2</p>
            <p className="text-sm font-bold">{aggregateStats.tier2.toLocaleString()}</p>
          </div>
          <div className="flex-1 flex flex-col items-center">
            <div 
              className="w-full bg-amber-500 transition-all" 
              style={{ height: `${Math.min(100, (aggregateStats.tier3 / (aggregateStats.tier1 || 1)) * 100)}%` }}
            ></div>
            <p className="text-xs mt-2 text-slate-600">Tier 3</p>
            <p className="text-sm font-bold">{aggregateStats.tier3.toLocaleString()}</p>
          </div>
          <div className="flex-1 flex flex-col items-center">
            <div 
              className="w-full bg-red-600 transition-all" 
              style={{ height: `${Math.min(100, (aggregateStats.tier4 / (aggregateStats.tier1 || 1)) * 100)}%` }}
            ></div>
            <p className="text-xs mt-2 text-slate-600">Tier 4</p>
            <p className="text-sm font-bold">{aggregateStats.tier4}</p>
          </div>
        </div>
      </div>

      {/* Platform List */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
            Platform Rankings ({filteredPlatforms.length})
          </p>
          <p className="text-xs text-slate-500">Click a platform for detailed metrics</p>
        </div>

        {filteredPlatforms.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200">
            <p className="text-slate-500">No platforms match your filters</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPlatforms
              .sort((a, b) => b.score - a.score)
              .map((platform, index) => {
                const IconComponent = categoryIcons[platform.category] || Shield;
                const isSelected = selectedPlatform?.id === platform.id;
                
                return (
                  <div key={platform.id}>
                    <div 
                      className={`bg-white border ${isSelected ? 'border-cyan-600 ring-2 ring-cyan-100' : 'border-slate-200'} p-4 cursor-pointer hover:border-slate-400 transition-all`}
                      onClick={() => setSelectedPlatform(isSelected ? null : platform)}
                      data-testid={`platform-row-${platform.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                          <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{platform.name}</h4>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <span>{platform.category}</span>
                              <span>•</span>
                              <span>{platform.location}</span>
                              <span>•</span>
                              <span>{platform.ageGroups.join(', ')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="flex items-center gap-2">
                              <span className={`text-2xl font-bold ${getScoreColor(platform.score).split(' ')[0]}`}>
                                {platform.score}
                              </span>
                              {getTrendIcon(platform.trend)}
                              <span className="text-sm text-slate-500">{platform.change}</span>
                            </div>
                            <p className="text-xs text-slate-400">SAFEScore</p>
                          </div>
                          <span className={`${getCertBadgeColor(platform.certLevel)} text-xs px-3 py-1 font-semibold uppercase tracking-wider`}>
                            {platform.certLevel}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Metrics */}
                    {isSelected && (
                      <div className="bg-slate-100 border border-t-0 border-slate-200 p-6 animate-fade-in">
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                          <div className="bg-white p-4 text-center">
                            <p className="text-2xl font-bold text-slate-900">{platform.metrics.threatsBlocked.toLocaleString()}</p>
                            <p className="text-xs text-slate-500 uppercase tracking-wider">Total Blocked</p>
                          </div>
                          <div className="bg-white p-4 text-center">
                            <p className="text-2xl font-bold text-emerald-600">{platform.metrics.threatsBlockedToday}</p>
                            <p className="text-xs text-slate-500 uppercase tracking-wider">Blocked Today</p>
                          </div>
                          <div className="bg-white p-4 text-center">
                            <p className="text-2xl font-bold text-cyan-600">{platform.metrics.avgResponseTime}</p>
                            <p className="text-xs text-slate-500 uppercase tracking-wider">Avg Response</p>
                          </div>
                          <div className="bg-white p-4 text-center">
                            <p className="text-2xl font-bold text-amber-600">{platform.metrics.falsePositiveRate}%</p>
                            <p className="text-xs text-slate-500 uppercase tracking-wider">False Positive</p>
                          </div>
                          <div className="bg-white p-4 text-center">
                            <p className="text-2xl font-bold text-red-600">{platform.metrics.groomingDetected}</p>
                            <p className="text-xs text-slate-500 uppercase tracking-wider">Grooming Detected</p>
                          </div>
                          <div className="bg-white p-4 text-center">
                            <p className="text-2xl font-bold text-purple-600">{platform.metrics.extortionPrevented}</p>
                            <p className="text-xs text-slate-500 uppercase tracking-wider">Extortion Blocked</p>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Intervention Breakdown</p>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="bg-slate-200 p-2 text-center">
                              <p className="font-bold">{platform.metrics.interventionsTier1.toLocaleString()}</p>
                              <p className="text-xs text-slate-600">Tier 1 (Awareness)</p>
                            </div>
                            <div className="bg-cyan-100 p-2 text-center">
                              <p className="font-bold">{platform.metrics.interventionsTier2.toLocaleString()}</p>
                              <p className="text-xs text-cyan-700">Tier 2 (Boundary)</p>
                            </div>
                            <div className="bg-amber-100 p-2 text-center">
                              <p className="font-bold">{platform.metrics.interventionsTier3}</p>
                              <p className="text-xs text-amber-700">Tier 3 (Escalation)</p>
                            </div>
                            <div className="bg-red-100 p-2 text-center">
                              <p className="font-bold">{platform.metrics.interventionsTier4}</p>
                              <p className="text-xs text-red-700">Tier 4 (Crisis)</p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>Last audit: {platform.lastAudit}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            <span>Age groups: {platform.ageGroups.join(', ')}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Footer Note */}
      <div className="bg-slate-100 border-t border-slate-200 p-4 text-center">
        <p className="text-xs text-slate-500">
          * Dashboard shows sample data for demonstration. Live metrics will display when platforms are certified.
        </p>
      </div>
    </div>
  );
};
